import {
  BooleanIcon,
  CodeIcon,
  NumberIcon,
  TextStringIcon,
  TimeIcon,
} from "@hightouch/htui";
import {
  type IntervalSchedule,
  ScheduleIntervalUnit,
  ScheduleType,
} from "src/components/schedule/types";
import { ConditionType } from "src/types/visual";
import {
  type JourneyExitCriteriaConfig,
  JourneyNodeSyncMode,
  type JourneySyncExitConfig,
  JourneyNodeType,
} from "src/types/journeys";
import type { JourneyContextConfig } from "src/pages/journeys/types";
import type { NullableProperties } from "src/types/utils";
import type {
  JourneyState,
  JourneyTemplateState,
  ExecutableJourneyNodeType,
  NodeColor,
} from "./types";

export const DRAWER_WIDTH = 672;
export const NODE_WIDTH = 300;
export const BASE_NODE_HEIGHT = 150;
export const MESSAGE_NODE_HEIGHT = 300;
export const NODE_DETAIL_HEIGHT = 50;
export const NODE_SPACING = 100;
// What ELK puts between siblings in a layer.
export const NODE_SIBLING_SPACING = 30;
export const MINI_NODE_MIN_WIDTH = 100;
export const MINI_NODE_MIN_HEIGHT = 75;
export const MINI_NODE_INITIAL_SPACING = 150 * 2;
const SIDEBAR_PADDING = 4;
export const MAX_CARD_WIDTH = 280 + SIDEBAR_PADDING * 4;
export const VISIBLE_SYNC_COUNT = 3;
export const CLONE_NODE_OFFSET = 150;
export const MINI_MAP_WIDTH = 200;
export const MINI_MAP_HEIGHT = 140;
export const CANVAS_HEADER_HEIGHT = 65;
export const CANVAS_FOOTER_HEIGHT = 64;
export const NODE_COMPACT_SPACING = 20;
export const DROP_AREA_HEIGHT = 106;
export const NODE_DRAG_THRESHOLD = 5;

/**
 * Types whose tile connects straight to several downstream tiles; every other
 * multi-path type fans out through branch nodes. Not imported from the
 * envelope's FAN_OUT_NODE_TYPES, which would pull zod into the app bundle.
 */
export const JOURNEY_NODES_WITH_DIRECT_FAN_OUT: ExecutableJourneyNodeType[] = [
  JourneyNodeType.Decision,
];

// The save rejects a decision with fewer arms.
export const DECISION_MIN_ARMS = 2;

export const NODE_COLORS: Record<ExecutableJourneyNodeType, NodeColor> = {
  [JourneyNodeType.Decision]: "indigo",
  [JourneyNodeType.Splits]: "indigo",
  [JourneyNodeType.SplitBranch]: "indigo",
  [JourneyNodeType.Segments]: "indigo",
  [JourneyNodeType.SegmentBranch]: "indigo",
  [JourneyNodeType.WaitUntilEvent]: "yellow",
  [JourneyNodeType.WaitUntilEventBranch]: "yellow",
  [JourneyNodeType.TimeDelay]: "yellow",
  [JourneyNodeType.Context]: "orange",
  [JourneyNodeType.CouponAssignment]: "orange",
  [JourneyNodeType.EntryCohort]: "green",
  [JourneyNodeType.EntryEvent]: "green",
  [JourneyNodeType.EntryAudienceExperiment]: "green",
  [JourneyNodeType.EntryAudienceExperimentBranch]: "green",
  [JourneyNodeType.Sync]: "teal",
  [JourneyNodeType.Function]: "teal",
  [JourneyNodeType.EventEmission]: "teal",
  [JourneyNodeType.Message]: "teal",
  [JourneyNodeType.NoOp]: "gray",
};

// Notes
export const NOTE_ATTACHED_MIN_HEIGHT = 80;
export const MAX_VISIBLE_NOTES_ON_NODE = 3;

// Handles
export const HANDLE_SIZE = 16;
export const FLOATING_HANDLE_SIZE = 20;
export const FLOATING_CONNECTION_HEIGHT = 65;

export const NO_PARENT_MODEL_ID = -1;

export const LIVE_STATES: (JourneyState | JourneyTemplateState)[] = [
  "active",
  "draining",
];
export const HALTED_STATES: (JourneyState | JourneyTemplateState)[] = [
  "off",
  "paused",
];
export const ACTIVATABLE_JOURNEY_STATES: (
  | JourneyState
  | JourneyTemplateState
)[] = ["draft", "staged", "draining", "off"];

export const DRAIN_MEMBERS_DESCRIPTION =
  "All active members will complete the journey and no new entries will occur";
export const HARD_STOP_DESCRIPTION =
  "All existing members will exit the journey and no new entries will occur";
export const PAUSED_DESCRIPTION =
  "All existing members will remain in their current tile and no new entries will occur";

export enum JourneyFilterType {
  // Draining are purposely left out
  ALL = "all",
  DRAFTS = "draft",
  ACTIVE = "active",
  OFF = "off",
  PAUSED = "paused",
  SCHEDULED = "scheduled",
}

export const JOURNEY_ENTRY_NODES: ExecutableJourneyNodeType[] = [
  JourneyNodeType.EntryEvent,
  JourneyNodeType.EntryCohort,
  JourneyNodeType.EntryAudienceExperiment,
];

export const JOURNEY_NODES_WITH_BRANCH_NODES: ExecutableJourneyNodeType[] = [
  JourneyNodeType.Segments,
  JourneyNodeType.WaitUntilEvent,
  JourneyNodeType.Splits,
  JourneyNodeType.EntryAudienceExperiment,
];

export const JOURNEY_BRANCH_NODES: ExecutableJourneyNodeType[] = [
  JourneyNodeType.SegmentBranch,
  JourneyNodeType.WaitUntilEventBranch,
  JourneyNodeType.SplitBranch,
  JourneyNodeType.EntryAudienceExperimentBranch,
];

export const PARENT_NODE_TO_BRANCH_NODE = {
  [JourneyNodeType.Segments]: JourneyNodeType.SegmentBranch,
  [JourneyNodeType.WaitUntilEvent]: JourneyNodeType.WaitUntilEventBranch,
  [JourneyNodeType.Splits]: JourneyNodeType.SplitBranch,
  [JourneyNodeType.EntryAudienceExperiment]:
    JourneyNodeType.EntryAudienceExperimentBranch,
  // A Function node has these only while it has a success condition, so callers check the node, not the type.
  [JourneyNodeType.Function]: JourneyNodeType.SegmentBranch,
};

// Actions that should not dirty the form.
export const NOOP_REACTFLOW_CHANGES = ["dimensions", "select"];

export const DEFAULT_INTERVAL_SCHEDULE: IntervalSchedule = {
  type: ScheduleType.INTERVAL,
  schedule: {
    interval: {
      quantity: 1,
      unit: ScheduleIntervalUnit.HOUR,
    },
  },
};

export const NULL_EXIT_CRITERIA: JourneyExitCriteriaConfig = {
  type: ConditionType.And,
  conditions: [],
};

export const EMPTY_CONTEXT_CONFIG: JourneyContextConfig = {
  variables: [],
};

export const PERFORMANCE_TABLE_HEADER_STYLES = {
  mb: 1,
  textTransform: "none",
  borderBottomWidth: "hairline",
  borderBottomStyle: "solid",
};

export const PERFORMANCE_TABLE_RIGHT_ALIGNED_HEADER_STYLES = {
  display: "flex",
  justifyContent: "flex-end",
  paddingRight: "2 !important",
  pl: "0 !important",
};

export const PERFORMANCE_TABLE_CELL_STYLES = {
  borderBottom: "none",
};

export const PERFORMANCE_TABLE_RIGHT_ALIGNED_CELL_STYLES = {
  display: "flex",
  justifyContent: "end",
  pl: "0 !important",
  pr: "2 !important",
};

export const MINUTES_IN_HOUR = 60;
export const MINUTES_IN_DAY = 60 * 24;
export const MINUTES_IN_WEEK = 7 * 60 * 24;
export const MINUTES_IN_MONTH = 7 * 60 * 24 * 30;
export const MINUTES_IN_YEAR = 7 * 60 * 24 * 30 * 365;

export const initializeJourneySyncSettings: {
  mode: JourneyNodeSyncMode;
  exit_config: NullableProperties<JourneySyncExitConfig>;
} = {
  mode: JourneyNodeSyncMode.Cohort,
  exit_config: {
    remove_after: null,
    remove_on_journey_exit: true,
    expire_after: null,
  },
};

export const JOURNEY_VARIABLE_TYPE_ICON_MAP = {
  BOOLEAN: BooleanIcon,
  STRING: TextStringIcon,
  NUMBER: NumberIcon,
  TIMESTAMP: TimeIcon,
  JSON: CodeIcon,
};

export const NOT_SELECTED_VALUE = null;
